import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DashboardService } from '../../../../../service/events/dashboard.service';
import { BaseForm } from '../../../../base-form';
import { TareasApiService } from '../../../../../service/api/tareas-api.service';
import { FincasApiService } from '../../../../../service/api/fincas-api.service';
import { build, pool } from '../../../../../common/classes/request-builder';
import { environment } from '../../../../../../environments/environment';
import { StorageManager } from '../../../../../common/storage-manager.class';
import type {
    ReactiveFormChangedField,
    ReactiveFormValue,
} from '@fitogest/reactive-form-builder/interfaces';
import { OptionsComponent } from '../../../../../common/components/upload-file/upload-file.component';
import { EcorregimenModel } from '../../../../../models/tareas/ecorregimen.model';
import { ParcelasModel } from '../../../../../models/form-common/parcelas.model';
import { EcorregimenesFormDefinition } from './form-control/form-definition';

@Component({
    selector: 'app-ecorregimenes-form',
    standalone: false,
    templateUrl: './ecorregimenes-form.component.html',
    styleUrls: ['./ecorregimenes-form.component.scss'],
})
export class EcorregimenesFormComponent extends BaseForm<EcorregimenModel> implements OnInit {
    public readonly appName = environment.appName;

    public form!: EcorregimenesFormDefinition;
    public override model: EcorregimenModel = {};
    public formReady = false;
    public loadingData = true;
    public showDialogUploadFile = false;

    
    public optionsUploadFile: OptionsComponent = {
        name: 'u_' + (environment.features.hasClients ? StorageManager.getClient()?.id : StorageManager.getUser()?.id),
        directory: 'agroambientales',
        multiple: false,
        accept: '.pdf',
        maxFileSize: 2000000,
        mode: 'advanced',
        uploaded: false,
        uploadedFilesNames: [],
        url: environment.serverUrl + 'ws/index.php?p1=upload&p2=upload_file&folder=agroambientales',
    };

    constructor(
        public override dashboard: DashboardService,
        public tareasApi: TareasApiService,
        public override route: ActivatedRoute,
        public override router: Router,
        public fincasApi: FincasApiService,
    ) {
        super(
            route,
            router,
            dashboard,
            tareasApi.ecorregimenes.PUT,
            tareasApi.ecorregimenes.POST,
            'tareas-ecorregimenes',
            `Editar Tarea ${environment.features.moduleNameEcorregimenesOrAgroAmbiental[1]}`,
            `Realizar Tarea ${environment.features.moduleNameEcorregimenesOrAgroAmbiental[1]}`,
            `Duplicar Tarea ${environment.features.moduleNameEcorregimenesOrAgroAmbiental[1]}`,
        );
    }

    public async ngOnInit(): Promise<void> {
        this.softInit(this.getType());
        this.form = new EcorregimenesFormDefinition(this.getType(), this.getModel());
        this.form.model = this.model;

        await this.initFormData();

        this.optionsUploadFile.uploaded = Boolean(this.model.adjunto_guia);
        this.optionsUploadFile.uploadedFilesNames = this.model.adjunto_guia
            ? String(this.model.adjunto_guia).split(',').map((it) => it.trim()).filter((it) => it.length > 0)
            : [];

        this.formReady = true;
    }

    
    public formChanges(
        formModel: ReactiveFormValue<EcorregimenModel>,
        tag: ReactiveFormChangedField<EcorregimenModel>,
    ): void {
        const changedFieldName = String(tag);

        this.form.model = {
            ...this.form.model,
            ...formModel,
        } as EcorregimenModel;
        this.model = this.form.model as EcorregimenModel;

        if (changedFieldName === 'hora_inicio' || changedFieldName === 'hora_fin') {
            this.updateHorasTotales();
        }
    }

    public handleEventUpload(event: { originalEvent: unknown; files: File[] }): void {
        if (event.files.length === 0) {
            return;
        }

        const adjuntoGuia = event.files.map((file) => file.name).join(', ');

        this.model.adjunto_guia = adjuntoGuia;
        this.form.model.adjunto_guia = adjuntoGuia;
        this.optionsUploadFile.uploaded = true;
        this.optionsUploadFile.uploadedFilesNames = event.files.map((file) => file.name);
        this.formBuilderRef?.updateForm({ adjunto_guia: adjuntoGuia }, true);
    }


    private initFormData(): Promise<boolean> {
        this.loadingData = true;
        this.resetExternalDropdowns();

        return pool([
            build(this.fincasApi.fincasGET, this.form.fincas, 'nombre'),
            build(this.fincasApi.sectoresGET, this.form.sectores, 'nombre', true),
            build(
                this.fincasApi.parcelasGET,
                this.form.parcelas,
                this.appName === 'avafitogest' ? this.parcelaLabel : 'nombre',
                true,
            ),
        ], () => {
            this.dropdownLoadTrigger.next();
        }).then(() => {
            this.loadingData = false;
            this.finalizeExternalDropdowns();
            this.dropdownLoadTrigger.next();

            return true;
        }).catch((error: unknown) => {
            this.loadingData = false;
            this.finalizeExternalDropdowns();
            this.dropdownLoadTrigger.next();
            console.error('Error loading ecorregimenes form data.', error);

            return true;
        });
    }

    private updateHorasTotales(): void {
        const horasTotales = this.form.calculateHorasTotales();

        if (this.form.model.horas_totales === horasTotales) {
            return;
        }

        this.form.model = {
            ...this.form.model,
            horas_totales: horasTotales,
        };
        this.model = this.form.model as EcorregimenModel;

        this.formBuilderRef?.updateForm({ horas_totales: horasTotales }, true);
    }

    private parcelaLabel = (p: ParcelasModel): string => {
        const nombre = (p.nombre ?? '').toString().trim();
        const variedad = (p.variedad ?? 'SIN VARIEDAD').toString().trim();

        let zonaRiegoAbrev = '';

        if ((p.cultivo ?? '').toLowerCase() === 'arroz') {
            const zonaRiego = (p.zona_riego ?? '').toString().toLowerCase();

            if (zonaRiego.includes('alta')) {
                zonaRiegoAbrev = 'ZA';
            } else if (zonaRiego.includes('baja')) {
                zonaRiegoAbrev = 'ZB';
            }
        }

        const variedadConZona = zonaRiegoAbrev ? `${variedad} - ${zonaRiegoAbrev}` : variedad;
        const poligono = (p.poligono ?? '').toString().trim();
        const numero = (p.numero ?? '').toString().trim();
        const recinto = (p.recinto ?? '').toString().trim();
        const codigos = [poligono, numero, recinto].filter(Boolean).join('-');
        const superficieRaw = (p.superficie_cultivada ?? p.superficie_sigpac ?? p.superficie ?? '0').toString();
        const superficieNumber = parseFloat(superficieRaw.replace(',', '.')) || 0;
        const superficie = superficieNumber.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        const unidades = environment.features.textoSuperficie || 'ha';
        const derecha = [codigos || undefined, `${superficie} ${unidades}`].filter(Boolean).join(' · ');

        return `${nombre} - ${variedadConZona} — ${derecha}`;
    };
}
